import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Text,
  Stack,
  Group,
  Tabs,
  Box,
  Loader,
  Center,
  Menu,
  ActionIcon,
  Button,
  Avatar,
  Tooltip,
  Progress,
  TextInput,
  CloseButton,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { List, Grid, Upload, Plus, Search } from 'react-feather';
import { ButtonToggle } from '../../components/common';
import { CardsPerRowDropdown } from './components/CardsPerRowDropdown';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  ArrowLeft,
  Edit,
  Trash2,
  X,
  Copy,
  User,
  Calendar,
  Settings,
  ChevronRight,
  RotateCcw,
  Archive,
  RefreshCw,
  AlertCircle,
} from 'react-feather';
import { IconMailOff } from '@tabler/icons-react';
import classes from './CohortDetailPage.module.css';
import { cohortsApi, type StatusReadinessResponse } from '../../services/api/cohorts';
import { timeSlotsApi } from '../../services/api/timeSlots';
import type { CohortWithDetails, CohortStatus } from '../../types';
import { COHORT_STATUS_CONFIG } from '../../config/statusConfig';
import { HeaderPill } from './components/HeaderPill';
import { OverviewTab } from './components/OverviewTab';
import { ActivityTab } from './components/ActivityTab';
import { SessionsTab } from './components/SessionsTab';
import { EditCohortModal } from './components/EditCohortModal';
import { EditCohortDetailsModal } from './components/EditCohortDetailsModal';
import { EditSessionConfigModal } from './components/EditSessionConfigModal';
import { EditTeamAssignmentsModal } from './components/EditTeamAssignmentsModal';
import { EditDateConstraintsModal } from './components/EditDateConstraintsModal';
import { EditSessionsModal } from './components/EditSessionsModal';
import { EditPrimaryContactsModal } from './components/EditPrimaryContactsModal';
import { EditJobAssignmentsModal } from './components/EditJobAssignmentsModal';
import { DuplicateCohortModal } from './components/DuplicateCohortModal';
import { ParticipantsTab } from './components/ParticipantsTab';
import { TasksTab } from './components/TasksTab';
import { TaskModal } from './components/TaskModal';
import { ResourcesTab } from './components/ResourcesTab';
import { CoachesTab } from './components/CoachesTab';
import { SessionLeadersTab } from './components/SessionLeadersTab';
import { SurveysTab } from './components/surveys';
import { AttendanceFollowUpsPane } from './components/AttendanceFollowUpsPane';
import type { TaskWithDetails, TaskPhase, OverallHealthStatus } from '../../types';
import { useFeatureFlag } from '../../stores/featureFlags';
import { usePersonaStore } from '../../stores/persona';
import { useAuthStore } from '../../stores/auth';
import { clearTimeSlotRecoveryForCohort, pendingTimeSlotRecovery } from '../../utils/cohortWizardDraft';
import { PendingTimeSlotsNotice } from './CreateCohortPage/PendingTimeSlotsNotice';
import { cohortDeleteSuccessMessage } from './CreateCohortPage/timeSlotCreation';
import type { FormTimeSlot } from './CreateCohortPage/types';
import { isPeerLedGroupCoaching, isLiveSessionDeliveryEnabled } from '../../utils/liveSessionDelivery';
import { parseDateOnly } from '../../utils/dateHelpers';
import { useHealthSummary } from './hooks/useHealthSummary';

// MED-160: Status labels for Advance button
// Progression: Draft → In Design → In Prep → Live → Wrap Up → Completed
const ADVANCE_BUTTON_LABELS: Partial<Record<CohortStatus, string>> = {
  draft: 'Advance to In Design',
  in_design: 'Advance to In Prep',
  in_prep: 'Advance to Live',
  live: 'Advance to Wrap Up',
  wrap_up: 'Mark Completed',
};

// MED-160: Revert button labels
const REVERT_BUTTON_LABELS: Partial<Record<CohortStatus, string>> = {
  in_design: 'Revert to Draft',
  in_prep: 'Revert to In Design',
  live: 'Revert to In Prep',
  wrap_up: 'Revert to Live',
  completed: 'Revert to Wrap Up',
};

// MED-263: Advance button color = next status color
const ADVANCE_BUTTON_COLORS: Partial<Record<CohortStatus, string>> = {
  draft: 'surveyPurple',       // → In Design
  in_design: 'statusInPrep',   // → In Prep
  in_prep: 'statusLive',       // → Live
  live: 'medleyGreen',         // → Wrap Up
  wrap_up: 'statusCompleted',  // → Completed
};

// MED-263: Hex dot colors for status pills (from theme color[8])
const STATUS_DOT_COLORS: Record<CohortStatus, string> = {
  draft: '#E8E6E3',
  in_design: '#8B5CF6',
  in_prep: '#C1DBEF',
  live: '#F09A65',
  wrap_up: '#28463A',
  completed: '#4CAF50',
  cancelled: '#888888',
  archived: '#888888',
};

// MED-114: Health dot colors from rules engine
const HEALTH_DOT_COLORS: Record<OverallHealthStatus, string> = {
  at_risk: '#D64545',
  monitor: '#E9A23B',
  on_track: '#4A7C59',
  no_data: '#888888',
};

// MED-114: Health labels for header pill
const HEALTH_LABELS: Record<OverallHealthStatus, string> = {
  at_risk: 'At Risk',
  monitor: 'Monitor',
  on_track: 'Healthy',
  no_data: 'No Data',
};

// Avatar colors for Primary Partner Contacts
const CONTACT_AVATAR_COLORS = ['medleyGreen', 'teal', 'grape', 'blue'] as const;

// Valid tabs and sub-views for URL routing
// MED-906: 'sessionLeaders' is the peer_led replacement for 'coaches'; both are
// listed so deep links survive a modality flip and pre-load the right state.
const VALID_TABS = ['overview', 'tasks', 'surveys', 'sessions', 'coaches', 'sessionLeaders', 'participants', 'resources', 'settings', 'activity'];
const VALID_VIEWS: Record<string, string[]> = {
  participants: ['participants', 'groups'],
};

export function CohortDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cohort, setCohort] = useState<CohortWithDetails | null>(null);
  const [loading, setLoading] = useState(true);

  // E19: Feature flag for attendance follow-ups
  const attendanceFollowUpsEnabled = useFeatureFlag('attendance_follow_ups');

  // MED-160: Status readiness state
  const [statusReadiness, setStatusReadiness] = useState<StatusReadinessResponse | null>(null);

  // MED-114: Health summary for header pill
  const { healthSummary, refresh: refreshHealthSummary } = useHealthSummary({ cohortId: id || '' });
  const [statusActionLoading, setStatusActionLoading] = useState(false);
  // MED-1196 / B3: disables the purge menu item while a purge is in flight.
  const [purgeAiDataLoading, setPurgeAiDataLoading] = useState(false);

  // Active tab state - sync with URL query param
  const tabFromUrl = searchParams.get('tab');
  const viewFromUrl = searchParams.get('view');
  const participantUserIdFromUrl = searchParams.get('participantUserId');
  const detailTabFromUrl = searchParams.get('detailTab');
  const sessionIdFromUrl = searchParams.get('sessionId');
  const sessionTabFromUrl = searchParams.get('sessionTab');
  const eventIdFromUrl = searchParams.get('eventId');
  const taskIdFromUrl = searchParams.get('taskId');
  const initialTab = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'overview';
  const [activeTab, setActiveTab] = useState<string | null>(initialTab);

  // Handle tab change - update URL (preserve view param if staying on same tab)
  const handleTabChange = useCallback((value: string | null) => {
    setActiveTab(value);
    const newParams: Record<string, string> = {};
    if (value && value !== 'overview') {
      newParams.tab = value;
    }
    // Preserve view param only if the new tab supports it
    if (value && viewFromUrl && VALID_VIEWS[value]?.includes(viewFromUrl)) {
      newParams.view = viewFromUrl;
    }
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams, viewFromUrl]);

  // Sync activeTab with URL when navigating (browser back/forward)
  useEffect(() => {
    const newTab = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'overview';
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [tabFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear auto-open participant params after the detail modal opens
  const handleAutoOpenComplete = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('participantUserId');
      next.delete('detailTab');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Clear auto-open session params after the session detail drawer opens
  const handleSessionAutoOpenComplete = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('sessionId');
      next.delete('sessionTab');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // MED-1348: clear the event param after the event drawer opens
  const handleEventAutoOpenComplete = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('eventId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Modal controls
  const [editModalOpened, { close: closeEditModal }] = useDisclosure(false);
  const [editDetailsModalOpened, { open: openEditDetailsModal, close: closeEditDetailsModal }] = useDisclosure(false);
  const [sessionConfigModalOpened, { open: openSessionConfigModal, close: closeSessionConfigModal }] = useDisclosure(false);
  const [teamModalOpened, { open: openTeamModal, close: closeTeamModal }] = useDisclosure(false);
  const [constraintsModalOpened, { open: openConstraintsModal, close: closeConstraintsModal }] = useDisclosure(false);
  const [sessionsModalOpened, { close: closeSessionsModal }] = useDisclosure(false);
  const [primaryContactsModalOpened, { open: openPrimaryContactsModal, close: closePrimaryContactsModal }] = useDisclosure(false);
  const [jobAssignmentsModalOpened, { open: openJobAssignmentsModal, close: closeJobAssignmentsModal }] = useDisclosure(false);
  const [duplicateModalOpened, { open: openDuplicateModal, close: closeDuplicateModal }] = useDisclosure(false);
  const [taskModalOpened, { open: openTaskModal, close: closeTaskModal }] = useDisclosure(false);
  const [followUpsOpened, { open: openFollowUpsDrawer, close: closeFollowUpsDrawer }] = useDisclosure(false);
  const [followUpSessionId, setFollowUpSessionId] = useState<string | undefined>();
  const [followUpParticipantId, setFollowUpParticipantId] = useState<string | undefined>();

  // E19-6: Open follow-ups pane with optional session/participant targeting
  const openFollowUps = useCallback((options?: { sessionId?: string; participantId?: string }) => {
    setFollowUpSessionId(options?.sessionId);
    setFollowUpParticipantId(options?.participantId);
    openFollowUpsDrawer();
  }, [openFollowUpsDrawer]);

  const closeFollowUps = useCallback(() => {
    closeFollowUpsDrawer();
    setFollowUpSessionId(undefined);
    setFollowUpParticipantId(undefined);
  }, [closeFollowUpsDrawer]);
  const [selectedTask, setSelectedTask] = useState<TaskWithDetails | null>(null);
  const [defaultPhase, setDefaultPhase] = useState<TaskPhase | null>(null);
  const [taskListKey, setTaskListKey] = useState(0);
  const [sessionsRefreshKey, setSessionsRefreshKey] = useState(0);

  // MED-204: Track when overview tab needs refresh (after task status changes)
  const [overviewRefreshKey, setOverviewRefreshKey] = useState(0);

  // MED-363: Groups prerequisites check (time slots)
  const [hasTimeSlots, setHasTimeSlots] = useState<boolean | null>(null);
  const hasSessionDates = (cohort?.sessions?.length ?? 0) > 0;
  const hasParticipants = (cohort?.participant_count ?? 0) > 0;
  const groupsPrereqsMet = hasSessionDates && hasTimeSlots === true;

  // MED-906 / E4.0c: peer_led cohorts swap the Coaches tab for the Session
  // Leaders tab. Defaulted to false until cohort + modalities load.
  // MED-1202: routed through the shared selector — "peer-led = no coaches"
  // follows the coaching sub-type alone (independent of Live Session Delivery),
  // consistent with the Groups/Sessions views and the backend.
  const isPeerLedCohort = isPeerLedGroupCoaching(cohort);

  // MED-1268: while the Create Cohort wizard holds a list of time slots that
  // did not save for this cohort, the page says so and links back to the retry.
  const userId = useAuthStore(state => state.user?.id ?? null);
  const pendingTimeSlots = id ? pendingTimeSlotRecovery<FormTimeSlot>(localStorage, userId, id) : null;

  // MED-1196 / B3: the internal-admin-only "Purge AI Data" control. Visibility
  // is gated on the internal_admin persona AND both Live Session Delivery
  // switches (the global flag + this cohort's group-coaching modality). The
  // server independently enforces internal_admin + the flag (AC1/AC6); this
  // governs visibility only.
  const { isFullAdmin } = usePersonaStore();
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');
  const canPurgeAiData =
    isFullAdmin && liveSessionDeliveryEnabled && isLiveSessionDeliveryEnabled(cohort);

  // MED-906: normalize the active tab against the cohort's modality after the
  // cohort loads. A user landing via `?tab=coaches` on a peer_led cohort would
  // otherwise see no panel content (the Coaches panel is unrendered), and vice
  // versa.
  useEffect(() => {
    if (!cohort) return;
    if (isPeerLedCohort && activeTab === 'coaches') {
      setActiveTab('sessionLeaders');
      setSearchParams({ tab: 'sessionLeaders' }, { replace: true });
    } else if (!isPeerLedCohort && activeTab === 'sessionLeaders') {
      setActiveTab('coaches');
      setSearchParams({ tab: 'coaches' }, { replace: true });
    }
  }, [isPeerLedCohort, activeTab, cohort, setSearchParams]);

  // MED-348: Edit Cohort Details with specific section expansion
  const [editDetailsSections, setEditDetailsSections] = useState<string[] | undefined>(undefined);

  const handleOpenEditDetailsForTimeSlots = useCallback(() => {
    setEditDetailsSections(['time-slots']);
    openEditDetailsModal();
  }, [openEditDetailsModal]);

  // MED-363: Open edit details for groups prereqs (key dates + time slots)
  const handleOpenEditDetailsForGroupsPrereqs = useCallback(() => {
    const sections: string[] = [];
    if (!hasSessionDates) sections.push('key-dates');
    if (!hasTimeSlots) sections.push('time-slots');
    setEditDetailsSections(sections.length > 0 ? sections : ['key-dates', 'time-slots']);
    openEditDetailsModal();
  }, [openEditDetailsModal, hasSessionDates, hasTimeSlots]);

  const handleCloseEditDetails = useCallback(() => {
    setEditDetailsSections(undefined);
    closeEditDetailsModal();
  }, [closeEditDetailsModal]);

  // Participants tab sub-view state (lifted up for sub-header) - sync with URL
  const displayFromUrl = searchParams.get('display');
  const initialParticipantsView = viewFromUrl === 'groups' ? 'groups' : 'participants';
  const initialDisplayMode = displayFromUrl === 'card' ? 'card' : 'list';
  const [participantsViewMode, setParticipantsViewMode] = useState<'participants' | 'groups'>(initialParticipantsView);
  const [groupsDisplayMode, setGroupsDisplayMode] = useState<'list' | 'card'>(initialDisplayMode);
  const [cardsPerRow, setCardsPerRow] = useState(2);
  const [filterBarLeftEl, setFilterBarLeftEl] = useState<HTMLDivElement | null>(null);
  const [filterBarRightEl, setFilterBarRightEl] = useState<HTMLDivElement | null>(null);
  const [participantsHandlers, setParticipantsHandlers] = useState<{
    openImportModal: () => void;
    openUploadGroupsModal: () => void;
    openAddGroupModal?: () => void;
  } | null>(null);

  // Groups search state (client-side filtering)
  const [groupsSearchValue, setGroupsSearchValue] = useState('');
  const [debouncedGroupsSearch] = useDebouncedValue(groupsSearchValue, 300);

  // Sync participantsViewMode and displayMode with URL when navigating
  useEffect(() => {
    if (activeTab === 'participants') {
      const newView = viewFromUrl === 'groups' ? 'groups' : 'participants';
      if (newView !== participantsViewMode) {
        setParticipantsViewMode(newView);
      }
      const newDisplay = displayFromUrl === 'card' ? 'card' : 'list';
      if (newDisplay !== groupsDisplayMode) {
        setGroupsDisplayMode(newDisplay);
      }
    }
  }, [viewFromUrl, displayFromUrl, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle participants view mode change - update URL
  const handleParticipantsViewChange = useCallback((view: 'participants' | 'groups') => {
    setParticipantsViewMode(view);
    const newParams: Record<string, string> = { tab: 'participants' };
    if (view !== 'participants') {
      newParams.view = view;
    }
    // Preserve display mode if in groups view
    if (view === 'groups' && groupsDisplayMode !== 'list') {
      newParams.display = groupsDisplayMode;
    }
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams, groupsDisplayMode]);

  // Handle display mode change - update URL
  const handleDisplayModeChange = useCallback((display: 'list' | 'card') => {
    setGroupsDisplayMode(display);
    const newParams: Record<string, string> = { tab: 'participants' };
    if (participantsViewMode !== 'participants') {
      newParams.view = participantsViewMode;
    }
    if (display !== 'list') {
      newParams.display = display;
    }
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams, participantsViewMode]);

  // Handle opening task modal for create/edit
  // MED-162: Added defaultPhase parameter for auto-selecting phase when adding from within a phase
  const handleOpenTaskModal = (task?: TaskWithDetails, phase?: TaskPhase) => {
    setSelectedTask(task || null);
    setDefaultPhase(phase || null);
    openTaskModal();
  };

  // Handle task saved - refresh the list and cohort counts
  const handleTaskSaved = () => {
    setTaskListKey(prev => prev + 1);
    refetchCohort();
  };

  // MED-160: Fetch status readiness data
  // NOTE: Must be defined before handleTaskStatusChanged which depends on it
  const fetchStatusReadiness = useCallback(async () => {
    if (!id) return;
    try {
      const readiness = await cohortsApi.getStatusReadiness(id);
      setStatusReadiness(readiness);
    } catch (error) {
      console.error('Failed to fetch status readiness:', error);
    }
  }, [id]);

  // MED-1355: the Coaches tab badge takes its number from the coach list, so
  // the two always agree after an add or a remove. Both count the same rows
  // (cohort_coaches not deleted and active).
  const handleCoachCountChange = useCallback((count: number) => {
    setCohort(prev => (prev && prev.coach_count !== count ? { ...prev, coach_count: count } : prev));
  }, []);

  // Re-fetch the full cohort (with counts, sessions, etc.) after mutations
  const refetchCohort = useCallback(async () => {
    if (!id) return;
    try {
      const data = await cohortsApi.getById(id);
      setCohort(data);
    } catch (error) {
      console.error('Failed to refetch cohort:', error);
    }
  }, [id]);

  // MED-204: Handle task status change - refresh status readiness, overview stats, AND cohort counts
  const handleTaskStatusChanged = useCallback(() => {
    fetchStatusReadiness();
    setOverviewRefreshKey(k => k + 1);
    refetchCohort();
  }, [fetchStatusReadiness, refetchCohort]);

  // Handle cohort update that may affect tasks and sessions (e.g., date changes triggering due date regeneration, key date sync to cohort events)
  const handleCohortUpdateWithTaskRefresh = (updatedCohort: CohortWithDetails) => {
    // Merge with existing cohort to preserve fields (like modalities) that partial API responses may omit
    setCohort(prev => prev ? { ...prev, ...updatedCohort } : updatedCohort);
    // Refresh tasks list in case due dates were regenerated
    setTaskListKey(prev => prev + 1);
    // Refresh sessions tab in case key dates changed (syncs to cohort_events)
    setSessionsRefreshKey(prev => prev + 1);
    // MED-363: Refresh time slots in case they were added/removed
    fetchTimeSlots();
  };

  // MED-341: Handle attendance completion - refresh health summary + overview stats
  const handleAttendanceComplete = useCallback(() => {
    refreshHealthSummary();
    setOverviewRefreshKey(k => k + 1);
  }, [refreshHealthSummary]);

  // Handle session update - update the session in the cohort's sessions array
  const handleSessionUpdate = (updatedSession: import('../../types').Session) => {
    if (!cohort) return;
    setCohort({
      ...cohort,
      sessions: cohort.sessions?.map(s =>
        s.id === updatedSession.id ? updatedSession : s
      ),
    });
  };

  useEffect(() => {
    const fetchCohort = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await cohortsApi.getById(id);
        setCohort(data);
      } catch (error) {
        // MED-1268: the cohort no longer exists, so a list of its unsaved time
        // slots can never be saved.
        if ((error as { status?: number }).status === 404) {
          clearTimeSlotRecoveryForCohort(localStorage, useAuthStore.getState().user?.id, id);
        }
        notifications.show({
          title: 'Error',
          message: error instanceof Error ? error.message : 'Failed to fetch cohort',
          color: 'red',
        });
        navigate('/cohorts');
      } finally {
        setLoading(false);
      }
    };

    fetchCohort();
  }, [id, navigate]);

  // MED-160: Fetch status readiness whenever cohort status changes
  useEffect(() => {
    if (cohort) {
      fetchStatusReadiness();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohort?.status, fetchStatusReadiness]);

  // MED-363: Fetch time slots count for groups prerequisites check
  const fetchTimeSlots = useCallback(async () => {
    if (!id) return;
    try {
      const response = await timeSlotsApi.list(id);
      setHasTimeSlots(response.time_slots.length > 0);
    } catch {
      setHasTimeSlots(false);
    }
  }, [id]);

  useEffect(() => {
    if (cohort) {
      fetchTimeSlots();
    }
  }, [cohort, fetchTimeSlots]);

  // MED-160: Advance cohort to next status
  const handleAdvanceStatus = async () => {
    if (!cohort || !id || !statusReadiness?.nextStatus) return;

    setStatusActionLoading(true);
    try {
      const result = await cohortsApi.advanceStatus(id);
      await refetchCohort();
      notifications.show({
        title: 'Status Advanced',
        message: `Cohort advanced to ${COHORT_STATUS_CONFIG[result.newStatus].label}`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to advance status',
        color: 'red',
      });
    } finally {
      setStatusActionLoading(false);
    }
  };

  // MED-160: Revert cohort to previous status
  const handleRevertStatus = async () => {
    if (!cohort || !id || !statusReadiness?.previousStatus) return;

    modals.openConfirmModal({
      title: 'Revert Status',
      children: (
        <Text size="sm">
          Revert cohort status from <strong>{COHORT_STATUS_CONFIG[cohort.status].label}</strong> to{' '}
          <strong>{COHORT_STATUS_CONFIG[statusReadiness.previousStatus].label}</strong>?
          Tasks will not be affected.
        </Text>
      ),
      labels: { confirm: 'Revert', cancel: 'Cancel' },
      confirmProps: { color: 'orange' },
      onConfirm: async () => {
        setStatusActionLoading(true);
        try {
          const result = await cohortsApi.revertStatus(id);
          await refetchCohort();
          notifications.show({
            title: 'Status Reverted',
            message: `Cohort reverted to ${COHORT_STATUS_CONFIG[result.newStatus].label}`,
            color: 'green',
          });
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to revert status',
            color: 'red',
          });
        } finally {
          setStatusActionLoading(false);
        }
      },
    });
  };

  // MED-160: Cancel cohort
  const handleCancelCohort = async () => {
    if (!cohort || !id) return;

    modals.openConfirmModal({
      title: 'Cancel Cohort',
      children: (
        <Text size="sm">
          Cancel this cohort? It will be hidden from active lists but can be restored later.
        </Text>
      ),
      labels: { confirm: 'Cancel Cohort', cancel: 'Keep Active' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setStatusActionLoading(true);
        try {
          await cohortsApi.cancelCohort(id);
          await refetchCohort();
          notifications.show({
            title: 'Cohort Cancelled',
            message: 'Cohort has been cancelled. You can restore it from the Settings menu.',
            color: 'orange',
          });
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to cancel cohort',
            color: 'red',
          });
        } finally {
          setStatusActionLoading(false);
        }
      },
    });
  };

  // MED-1196 / B3: internal-admin-only purge of all AI-generated data for the
  // cohort. Guarded by an in-app confirm modal (never a native confirm() — AC4)
  // that spells out exactly what is deleted and what is kept. The server issues
  // real deletes across every session's AI-output stores (AC2/AC7).
  const handlePurgeAiData = () => {
    if (!cohort || !id) return;

    modals.openConfirmModal({
      title: 'Purge AI Data',
      children: (
        <Stack gap="xs">
          <Text size="sm">
            Permanently delete <strong>all AI-generated data</strong> for this cohort across every
            session? This removes session transcripts, share-of-voice / airtime, discussion themes,
            and AI recap email drafts.
          </Text>
          <Text size="sm" c="dimmed">
            Sessions, attendance, session content, and participant-entered data (reflections,
            commitments, notes) are <strong>not</strong> affected. This action cannot be undone.
          </Text>
        </Stack>
      ),
      labels: { confirm: 'Purge AI Data', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setPurgeAiDataLoading(true);
        try {
          const result = await cohortsApi.purgeAiData(id);
          notifications.show({
            title: 'AI Data Purged',
            message:
              result.totalDeleted > 0
                ? `Removed ${result.totalDeleted} AI record(s) across ${result.sessionCount} session(s).`
                : 'No AI data was present for this cohort.',
            color: 'medleyGreen',
          });
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to purge AI data',
            color: 'red',
          });
        } finally {
          setPurgeAiDataLoading(false);
        }
      },
    });
  };

  // MED-160: Restore cancelled cohort
  const handleRestoreCohort = async () => {
    if (!cohort || !id) return;

    modals.openConfirmModal({
      title: 'Restore Cohort',
      children: (
        <Text size="sm">
          Restore this cohort to its previous status?
        </Text>
      ),
      labels: { confirm: 'Restore', cancel: 'Cancel' },
      confirmProps: { color: 'green' },
      onConfirm: async () => {
        setStatusActionLoading(true);
        try {
          const result = await cohortsApi.restoreCohort(id);
          await refetchCohort();
          notifications.show({
            title: 'Cohort Restored',
            message: `Cohort restored to ${COHORT_STATUS_CONFIG[result.newStatus].label}`,
            color: 'green',
          });
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to restore cohort',
            color: 'red',
          });
        } finally {
          setStatusActionLoading(false);
        }
      },
    });
  };

  // MED-160: Archive cohort (permanent)
  const handleArchiveCohort = async () => {
    if (!cohort || !id) return;

    modals.openConfirmModal({
      title: 'Archive Cohort',
      children: (
        <Text size="sm">
          Archive this cohort? <strong>This action cannot be undone.</strong> The cohort will be
          hidden from all lists and only accessible via search.
        </Text>
      ),
      labels: { confirm: 'Archive Permanently', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setStatusActionLoading(true);
        try {
          await cohortsApi.archiveCohort(id);
          await refetchCohort();
          notifications.show({
            title: 'Cohort Archived',
            message: 'Cohort has been permanently archived.',
            color: 'gray',
          });
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to archive cohort',
            color: 'red',
          });
        } finally {
          setStatusActionLoading(false);
        }
      },
    });
  };

  // MED-160: Handle advance button click with confirmation
  const handleAdvanceClick = () => {
    if (!cohort || !statusReadiness?.nextStatus) return;

    modals.openConfirmModal({
      title: `Advance to ${COHORT_STATUS_CONFIG[statusReadiness.nextStatus].label}`,
      children: (
        <Text size="sm">
          Move cohort to <strong>{COHORT_STATUS_CONFIG[statusReadiness.nextStatus].label}</strong> status?
        </Text>
      ),
      labels: { confirm: 'Advance', cancel: 'Cancel' },
      confirmProps: { color: 'medleyGreen' },
      onConfirm: handleAdvanceStatus,
    });
  };

  // Delete cohort with confirmation
  const handleDelete = () => {
    modals.openConfirmModal({
      title: 'Delete Cohort',
      children: (
        <Text size="sm">
          Are you sure you want to delete <strong>{cohort?.name}</strong>? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        if (!id) return;
        try {
          await cohortsApi.delete(id);
          // MED-1268: a deleted cohort keeps no list of unsaved time slots.
          // The message says so when a list was cleared.
          const clearedTimeSlotList = clearTimeSlotRecoveryForCohort(localStorage, userId, id);
          notifications.show({
            title: 'Success',
            message: cohortDeleteSuccessMessage(clearedTimeSlotList),
            color: 'green',
          });
          navigate('/cohorts');
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to delete cohort',
            color: 'red',
          });
        }
      },
    });
  };

  // Format date range (e.g., "Jan 14 - Apr 14, 2024")
  const formatDateRange = (start: string | null, end: string | null) => {
    if (!start) return 'Dates not set';
    // MED-1265: DATE columns arrive as UTC-midnight timestamps; parse as calendar dates
    const startDate = parseDateOnly(start)!;
    const endDate = end ? parseDateOnly(end) : null;
    const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!endDate) return startStr;
    const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  // Get cohort date range with fallback to key dates when session dates are not set
  const getCohortDateRange = () => {
    // First priority: session dates (start_date/end_date)
    if (cohort?.start_date) {
      return formatDateRange(cohort.start_date, cohort.end_date);
    }

    // Fallback: use cohort key dates (coach_immersion, participant_orientation, wrap_up)
    const keyDates: Date[] = [];

    if (cohort?.coach_immersion_date) {
      keyDates.push(new Date(cohort.coach_immersion_date));
    }
    if (cohort?.participant_orientation_date) {
      keyDates.push(new Date(cohort.participant_orientation_date));
    }
    if (cohort?.cohort_wrap_up_date) {
      keyDates.push(new Date(cohort.cohort_wrap_up_date));
    }

    if (keyDates.length === 0) {
      return 'Dates not set';
    }

    // Sort dates and get first/last
    keyDates.sort((a, b) => a.getTime() - b.getTime());
    const earliest = keyDates[0];
    const latest = keyDates[keyDates.length - 1];

    const startStr = earliest.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (keyDates.length === 1 || earliest.getTime() === latest.getTime()) {
      return startStr;
    }
    const endStr = latest.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  // Get initials for avatar
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Handle cohort duplication - navigate to new cohort
  const handleDuplicated = (newCohort: CohortWithDetails) => {
    navigate(`/cohorts/${newCohort.id}`);
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (!cohort) {
    return (
      <Container size="xl" py="xl">
        <Text>Cohort not found</Text>
      </Container>
    );
  }

  return (
    <>
      {/* Full-width Header */}
      <Box className={classes.fullWidthHeader}>
        <Container size="xl">
          {/* Header Content */}
          <Box className={classes.headerContent}>
            {/* Top Row: Back + Title + Pills + Progress + Advance + Settings */}
            <Group justify="space-between" mb="sm">
              <Group gap="md">
                {/* Back Button */}
                <ActionIcon
                  variant="filled"
                  color="medleyGreen"
                  size="lg"
                  radius="xl"
                  onClick={() => navigate('/cohorts')}
                >
                  <ArrowLeft size={18} />
                </ActionIcon>

                {/* Title + Status Pill + Health Pill */}
                <Group gap="sm">
                  <Text className={classes.cohortTitle}>{cohort.name}</Text>
                  <HeaderPill
                    dotColor={STATUS_DOT_COLORS[cohort.status]}
                    label={COHORT_STATUS_CONFIG[cohort.status].label}
                  />
                  {healthSummary && (
                    <HeaderPill
                      dotColor={HEALTH_DOT_COLORS[healthSummary.overallStatus] || '#888888'}
                      label={HEALTH_LABELS[healthSummary.overallStatus] || 'No Data'}
                    />
                  )}
                  {cohort.disabled_email_recipients?.length > 0 && (
                    <Tooltip label={`Emails disabled for: ${cohort.disabled_email_recipients.map((r: string) => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')}`}>
                      <Box style={{ display: 'flex', alignItems: 'center' }}>
                        <IconMailOff size={18} color="var(--mantine-color-gray-6)" />
                      </Box>
                    </Tooltip>
                  )}
                </Group>
              </Group>

              <Group gap="lg">
                {/* MED-263: Progress Bar + Advance Button */}
                {statusReadiness && cohort.status !== 'completed' && cohort.status !== 'cancelled' && cohort.status !== 'archived' && (
                  <Group gap="xs" align="center" style={{ minWidth: 180 }}>
                    <Box style={{ flex: 1 }}>
                      <Text size="xs" c="dimmed" mb={4}>
                        {statusReadiness.tasksCompleted} of {statusReadiness.tasksTotal}{' '}
                        {COHORT_STATUS_CONFIG[cohort.status].label} tasks ({statusReadiness.completionPercent}%)
                      </Text>
                      <Progress
                        value={statusReadiness.completionPercent}
                        color={ADVANCE_BUTTON_COLORS[cohort.status] || 'medleyGreen'}
                        size="sm"
                        radius="xl"
                      />
                    </Box>
                  </Group>
                )}

                {statusReadiness?.nextStatus && cohort.status !== 'completed' && cohort.status !== 'cancelled' && cohort.status !== 'archived' && (
                  <Tooltip
                    label={statusReadiness.readyToAdvance ? '' : 'Need 90% tasks complete to advance'}
                    disabled={statusReadiness.readyToAdvance}
                  >
                    <Button
                      color={ADVANCE_BUTTON_COLORS[cohort.status] || 'medleyGreen'}
                      rightSection={<ChevronRight size={16} />}
                      onClick={handleAdvanceClick}
                      disabled={!statusReadiness.readyToAdvance || statusActionLoading}
                      loading={statusActionLoading}
                    >
                      {ADVANCE_BUTTON_LABELS[cohort.status] || 'Advance'}
                    </Button>
                  </Tooltip>
                )}

                {/* Settings Button */}
                <Menu shadow="md" width={220} position="bottom-end">
                  <Menu.Target>
                    <Button variant="outline" leftSection={<Settings size={16} />}>
                      Settings
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item leftSection={<Edit size={14} />} onClick={openEditDetailsModal}>
                      Edit Cohort Details
                    </Menu.Item>
                    <Menu.Item leftSection={<Copy size={14} />} onClick={openDuplicateModal}>
                      Duplicate
                    </Menu.Item>

                    {/* MED-160: Revert option */}
                    {statusReadiness?.previousStatus && cohort.status !== 'cancelled' && cohort.status !== 'archived' && (
                      <>
                        <Menu.Divider />
                        <Menu.Item
                          color="orange"
                          leftSection={<RotateCcw size={14} />}
                          onClick={handleRevertStatus}
                        >
                          {REVERT_BUTTON_LABELS[cohort.status] || 'Revert Status'}
                        </Menu.Item>
                      </>
                    )}

                    {/* MED-160: Cancel/Restore/Archive options */}
                    <Menu.Divider />
                    {cohort.status === 'cancelled' ? (
                      <>
                        <Menu.Item
                          color="green"
                          leftSection={<RefreshCw size={14} />}
                          onClick={handleRestoreCohort}
                        >
                          Restore Cohort
                        </Menu.Item>
                        <Menu.Item
                          color="gray"
                          leftSection={<Archive size={14} />}
                          onClick={handleArchiveCohort}
                        >
                          Archive Cohort
                        </Menu.Item>
                      </>
                    ) : cohort.status === 'completed' ? (
                      <Menu.Item
                        color="gray"
                        leftSection={<Archive size={14} />}
                        onClick={handleArchiveCohort}
                      >
                        Archive Cohort
                      </Menu.Item>
                    ) : cohort.status !== 'archived' && (
                      <Menu.Item
                        color="red"
                        leftSection={<X size={14} />}
                        onClick={handleCancelCohort}
                      >
                        Cancel Cohort
                      </Menu.Item>
                    )}

                    {cohort.status !== 'archived' && (
                      <>
                        <Menu.Divider />
                        <Menu.Item color="red" leftSection={<Trash2 size={14} />} onClick={handleDelete}>
                          Delete Cohort
                        </Menu.Item>
                      </>
                    )}

                    {/* MED-1196 / B3: internal-admin-only AI-data retention purge. */}
                    {canPurgeAiData && (
                      <>
                        <Menu.Divider />
                        <Menu.Item
                          color="red"
                          leftSection={<Trash2 size={14} />}
                          onClick={handlePurgeAiData}
                          disabled={purgeAiDataLoading}
                        >
                          Purge AI Data
                        </Menu.Item>
                      </>
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>

            {/* Subtitle Row */}
            <Group className={classes.subtitle} ml={52}>
              <Group gap="xs">
                <User size={14} />
                <Text size="sm">{cohort.partner_name}</Text>
              </Group>
              <Group gap="xs">
                <Calendar size={14} />
                <Text size="sm">{getCohortDateRange()}</Text>
              </Group>
            </Group>
          </Box>

          {/* Tabs Row with Primary Contacts - now inside header */}
          <Box className={classes.tabsRow}>
            <Tabs value={activeTab} onChange={handleTabChange} className="tabs-underline">
              <Tabs.List>
                <Tabs.Tab value="overview">Overview</Tabs.Tab>
                <Tabs.Tab value="tasks">
                  Tasks {(cohort.task_count ?? 0) > 0 && (
                    <span className={classes.tabBadge}>{cohort.task_count}</span>
                  )}
                </Tabs.Tab>
                <Tabs.Tab value="surveys">Surveys</Tabs.Tab>
                <Tabs.Tab value="sessions">
                  Sessions <span className={classes.tabBadge}>{cohort.session_count || 0}</span>
                </Tabs.Tab>
                <Tabs.Tab value="participants">
                  Participants & Groups <span className={classes.tabBadge}>{cohort.participant_count}</span>
                </Tabs.Tab>
                {isPeerLedCohort ? (
                  <Tabs.Tab value="sessionLeaders">Session Leaders</Tabs.Tab>
                ) : (
                  <Tabs.Tab value="coaches">
                    Coaches <span className={classes.tabBadge}>{cohort.coach_count || 0}</span>
                  </Tabs.Tab>
                )}
                <Tabs.Tab value="resources">
                  Resources
                </Tabs.Tab>
                <Tabs.Tab value="activity">Activity</Tabs.Tab>
              </Tabs.List>
            </Tabs>

            {/* Primary Partner Contacts */}
            {cohort.primary_contacts && cohort.primary_contacts.length > 0 && (
              <Group gap="md" className={classes.primaryContacts}>
                <Text size="xs" c="dimmed">Primary Partner Contacts</Text>
                {cohort.primary_contacts.slice(0, 3).map((contact, index) => (
                  <Group key={contact.id} gap="xs" className={classes.contactItem}>
                    <Avatar size="sm" color={CONTACT_AVATAR_COLORS[index % CONTACT_AVATAR_COLORS.length]} radius="xl">
                      {getInitials(contact.first_name, contact.last_name)}
                    </Avatar>
                    <Box className={classes.contactInfo}>
                      <Text className={classes.contactName}>
                        {contact.first_name} {contact.last_name}
                      </Text>
                      {contact.job_title && (
                        <Text className={classes.contactTitle}>{contact.job_title}</Text>
                      )}
                    </Box>
                  </Group>
                ))}
              </Group>
            )}
          </Box>
        </Container>
      </Box>

      {/* Participants Sub-Header - separate bar below main header */}
      {activeTab === 'participants' && (
        <Box className={classes.subHeader}>
          <Container size="xl">
            <Group justify="space-between">
              {/* Participants / Groups toggle + search/filters */}
              <Group gap="sm">
                <ButtonToggle
                  options={[
                    { value: 'participants', label: 'Participants', icon: List },
                    { value: 'groups', label: 'Groups', icon: Grid, disabled: !hasParticipants, disabledTooltip: 'Upload participants first to create groups' },
                  ]}
                  value={participantsViewMode}
                  onChange={handleParticipantsViewChange}
                />
                {/* List / Card display toggle - only shown in Groups mode */}
                {participantsViewMode === 'groups' && (
                  <ButtonToggle
                    options={[
                      { value: 'list', label: 'List', icon: List },
                      { value: 'card', label: 'Card', icon: Grid },
                    ]}
                    value={groupsDisplayMode}
                    onChange={handleDisplayModeChange}
                  />
                )}
                {participantsViewMode === 'participants' && (
                  <div ref={setFilterBarLeftEl} style={{ display: 'contents' }} />
                )}
                {participantsViewMode === 'groups' && (
                  <TextInput
                    placeholder="Search groups..."
                    leftSection={<Search size={16} />}
                    rightSection={groupsSearchValue ? (
                      <CloseButton size="sm" onClick={() => setGroupsSearchValue('')} />
                    ) : undefined}
                    value={groupsSearchValue}
                    onChange={(e) => setGroupsSearchValue(e.currentTarget.value)}
                    w={250}
                  />
                )}
              </Group>

              <Group gap="sm">
                {participantsViewMode === 'participants' && (
                  <div ref={setFilterBarRightEl} style={{ display: 'contents' }} />
                )}
                {/* Cards per row dropdown - only shown in Groups card mode */}
                {participantsViewMode === 'groups' && groupsDisplayMode === 'card' && (
                  <CardsPerRowDropdown value={cardsPerRow} onChange={setCardsPerRow} />
                )}
                {participantsViewMode === 'groups' && (
                  <Tooltip
                    label="Configure session dates and time slots before uploading groups"
                    disabled={groupsPrereqsMet}
                  >
                    <Button
                      variant="outline"
                      color="gray"
                      bg="white"
                      style={{ borderColor: '#E8E6E3' }}
                      leftSection={<Upload size={16} />}
                      disabled={!groupsPrereqsMet}
                      onClick={() => participantsHandlers?.openUploadGroupsModal()}
                    >
                      {/* MED-1291 (AC4): was "Upload CSV". One label across both
                          entry points, and next to "Start Matching" the useful
                          distinction is groups-already-matched vs. run-the-matcher —
                          "CSV" named the file format, not the outcome. */}
                      Upload Matched Groups
                    </Button>
                  </Tooltip>
                )}
                {participantsViewMode === 'groups' && (
                  <Tooltip
                    label="Configure session dates and time slots before adding groups"
                    disabled={groupsPrereqsMet}
                  >
                    <Button
                      leftSection={<Plus size={16} />}
                      disabled={!groupsPrereqsMet}
                      onClick={() => participantsHandlers?.openAddGroupModal?.()}
                    >
                      Add Group
                    </Button>
                  </Tooltip>
                )}
              </Group>
            </Group>
          </Container>
        </Box>
      )}

      {/* MED-778: Persistent banner when audience features are disabled */}
      {(cohort.participant_features_enabled === false || cohort.coach_features_enabled === false) && (
        <Box
          style={{
            backgroundColor: 'var(--mantine-color-gray-1)',
            borderBottom: '1px solid var(--mantine-color-gray-3)',
          }}
          py="xs"
        >
          <Container size="xl">
            <Group gap="sm" justify="center">
              <AlertCircle size={16} color="var(--mantine-color-gray-6)" />
              <Text size="sm" c="gray.7" fw={500}>
                {cohort.participant_features_enabled === false && cohort.coach_features_enabled === false
                  ? 'Participant features disabled \u00B7 Coach features disabled'
                  : cohort.participant_features_enabled === false
                    ? 'Participant features disabled'
                    : 'Coach features disabled'}
              </Text>
            </Group>
          </Container>
        </Box>
      )}

      {/* MED-1307 (MDF-184): app access off while Participant Features is on */}
      {cohort.participant_features_enabled !== false && cohort.participant_app_access_enabled === false && (
        <Box
          style={{
            backgroundColor: 'var(--mantine-color-gray-1)',
            borderBottom: '1px solid var(--mantine-color-gray-3)',
          }}
          py="xs"
        >
          <Container size="xl">
            <Group gap="sm" justify="center" wrap="nowrap">
              <AlertCircle
                size={16}
                color="var(--mantine-color-gray-6)"
                style={{ flexShrink: 0 }}
              />
              <Text size="sm" c="gray.7" fw={500}>
                Participant app access is off. Participants get emails without links into the app.
              </Text>
            </Group>
          </Container>
        </Box>
      )}

      {/* Main Content */}
      <Container size="xl" py="xl">
        {pendingTimeSlots && (
          <PendingTimeSlotsNotice
            timeSlots={pendingTimeSlots.timeSlots}
            unsavedSlotIds={pendingTimeSlots.recovery.unsavedSlotIds}
            onReturnToRetry={() => navigate('/cohorts?create=true')}
          />
        )}

        {/* Tab Panels */}
        <Tabs value={activeTab} onChange={handleTabChange}>
        <Tabs.Panel value="overview" pt="lg">
          <OverviewTab
            cohort={cohort}
            onOpenSessionConfigModal={openSessionConfigModal}
            onOpenTeamModal={openTeamModal}
            onOpenConstraintsModal={openConstraintsModal}
            onOpenPrimaryContactsModal={openPrimaryContactsModal}
            onOpenJobAssignmentsModal={openJobAssignmentsModal}
            refreshKey={overviewRefreshKey}
            isActive={activeTab === 'overview'}
            onHealthChange={refreshHealthSummary}
            onOpenFollowUps={attendanceFollowUpsEnabled ? openFollowUps : undefined}
          />
        </Tabs.Panel>

        <Tabs.Panel value="activity" pt="lg">
          <ActivityTab
            cohort={cohort}
            isActive={activeTab === 'activity'}
          />
        </Tabs.Panel>

        <Tabs.Panel value="tasks" pt="lg">
          <TasksTab
            key={taskListKey}
            cohort={cohort}
            onOpenTaskModal={handleOpenTaskModal}
            onTaskStatusChanged={handleTaskStatusChanged}
            isActive={activeTab === 'tasks'}
            initialTaskId={activeTab === 'tasks' ? taskIdFromUrl : undefined}
            onTaskDrawerChange={(taskId) => {
              const newParams: Record<string, string> = {};
              if (activeTab && activeTab !== 'overview') newParams.tab = activeTab;
              if (taskId) newParams.taskId = taskId;
              setSearchParams(newParams, { replace: true });
            }}
          />
        </Tabs.Panel>

        <Tabs.Panel value="surveys" pt="lg">
          <SurveysTab
            cohort={cohort}
            isActive={activeTab === 'surveys'}
          />
        </Tabs.Panel>

        <Tabs.Panel value="sessions" pt="lg">
          <SessionsTab
            cohort={cohort}
            onSessionUpdate={handleSessionUpdate}
            onNavigateToParticipants={() => handleTabChange('participants')}
            onNavigateToGroups={() => {
              setActiveTab('participants');
              setParticipantsViewMode('groups');
              setSearchParams({ tab: 'participants', view: 'groups' }, { replace: true });
            }}
            isActive={activeTab === 'sessions'}
            refreshKey={sessionsRefreshKey}
            onAttendanceComplete={handleAttendanceComplete}
            autoOpenSessionId={sessionIdFromUrl || undefined}
            autoOpenSessionTab={sessionTabFromUrl || undefined}
            onSessionAutoOpenComplete={handleSessionAutoOpenComplete}
            autoOpenEventId={eventIdFromUrl || undefined}
            onEventAutoOpenComplete={handleEventAutoOpenComplete}
            onOpenFollowUps={attendanceFollowUpsEnabled ? openFollowUps : undefined}
          />
        </Tabs.Panel>

        {isPeerLedCohort ? (
          <Tabs.Panel value="sessionLeaders" pt="lg">
            <SessionLeadersTab cohort={cohort} isActive={activeTab === 'sessionLeaders'} />
          </Tabs.Panel>
        ) : (
          <Tabs.Panel value="coaches" pt="lg">
            <CoachesTab
              cohort={cohort}
              isActive={activeTab === 'coaches'}
              onEditCohortDetails={handleOpenEditDetailsForTimeSlots}
              onCoachCountChange={handleCoachCountChange}
            />
          </Tabs.Panel>
        )}

        <Tabs.Panel value="participants" pt={0}>
          <ParticipantsTab
            cohort={cohort}
            viewMode={participantsViewMode}
            groupsDisplayMode={groupsDisplayMode}
            cardsPerRow={cardsPerRow}
            registerHandlers={setParticipantsHandlers}
            isActive={activeTab === 'participants'}
            groupsSearchValue={debouncedGroupsSearch}
            hasSessionDates={hasSessionDates}
            hasTimeSlots={hasTimeSlots === true}
            onEditCohortDetails={handleOpenEditDetailsForGroupsPrereqs}
            onParticipantsChanged={refetchCohort}
            autoOpenParticipantUserId={participantUserIdFromUrl || undefined}
            defaultDetailTab={detailTabFromUrl || undefined}
            onAutoOpenComplete={handleAutoOpenComplete}
            filterBarPortalLeft={filterBarLeftEl}
            filterBarPortalRight={filterBarRightEl}
            onOpenFollowUps={attendanceFollowUpsEnabled ? openFollowUps : undefined}
          />
        </Tabs.Panel>

        <Tabs.Panel value="resources" pt="lg">
          <ResourcesTab cohort={cohort} isActive={activeTab === 'resources'} />
        </Tabs.Panel>

      </Tabs>

      {/* Modals */}
      <EditCohortModal
        opened={editModalOpened}
        onClose={closeEditModal}
        cohort={cohort}
        onUpdate={setCohort}
      />

      <EditCohortDetailsModal
        opened={editDetailsModalOpened}
        onClose={handleCloseEditDetails}
        cohort={cohort}
        onUpdate={handleCohortUpdateWithTaskRefresh}
        initialExpandedSections={editDetailsSections}
      />

      <EditSessionConfigModal
        opened={sessionConfigModalOpened}
        onClose={closeSessionConfigModal}
        cohort={cohort}
        onUpdate={setCohort}
      />

      <EditTeamAssignmentsModal
        opened={teamModalOpened}
        onClose={closeTeamModal}
        cohort={cohort}
        onUpdate={setCohort}
      />

      <EditDateConstraintsModal
        opened={constraintsModalOpened}
        onClose={closeConstraintsModal}
        cohort={cohort}
        onUpdate={setCohort}
      />

      <EditSessionsModal
        opened={sessionsModalOpened}
        onClose={closeSessionsModal}
        cohort={cohort}
        onUpdate={setCohort}
      />

      <EditPrimaryContactsModal
        opened={primaryContactsModalOpened}
        onClose={closePrimaryContactsModal}
        cohort={cohort}
        onUpdate={setCohort}
      />

      <EditJobAssignmentsModal
        opened={jobAssignmentsModalOpened}
        onClose={closeJobAssignmentsModal}
        cohort={cohort}
        onUpdate={setCohort}
      />

      <DuplicateCohortModal
        opened={duplicateModalOpened}
        onClose={closeDuplicateModal}
        cohort={cohort}
        onDuplicated={handleDuplicated}
      />

      <TaskModal
        opened={taskModalOpened}
        onClose={closeTaskModal}
        cohortId={cohort.id}
        task={selectedTask}
        defaultPhase={defaultPhase}
        onSaved={handleTaskSaved}
      />

      {attendanceFollowUpsEnabled && (
        <AttendanceFollowUpsPane
          opened={followUpsOpened}
          onClose={closeFollowUps}
          cohortId={cohort.id}
          initialSessionId={followUpSessionId}
          initialParticipantId={followUpParticipantId}
          participantFeaturesEnabled={cohort.participant_features_enabled}
        />
      )}
    </Container>
    </>
  );
}

export default CohortDetailPage;
